/**
 * OnboardingLinkCreator Component
 * Form for creating client onboarding links with form selection
 */

import React, { useState, useEffect } from 'react';
import { useOnboarding } from '../../../hooks/useOnboarding';
import { useFormsByPurpose } from '../../../hooks/useSecureForms';
import styles from './OnboardingLinkCreator.module.css';

const OnboardingLinkCreator = () => {
    const { createOnboardingLink, loading, error: hookError } = useOnboarding();
    const { forms: onboardingForms, loading: formsLoading } = useFormsByPurpose('client_onboarding');
    
    const [formData, setFormData] = useState({
        clientEmail: '',
        clientName: '',
        formId: '',
        prefillData: {
            companyName: '',
            industry: '',
            contactName: '',
            phone: ''
        }
    });
    
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);
    const [showPrefill, setShowPrefill] = useState(false);

    // Auto-select default form if available
    useEffect(() => {
        if (onboardingForms && onboardingForms.length > 0 && !formData.formId) {
            const defaultForm = onboardingForms.find(f => f.isDefault);
            if (defaultForm) {
                setFormData(prev => ({ ...prev, formId: defaultForm.formId }));
            }
        }
    }, [onboardingForms]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        setResult(null);

        try {
            const clientEmail = formData.clientEmail.trim();
            
            const request = {
                clientEmail: clientEmail,
                clientName: formData.clientName.trim() || undefined,
                formId: formData.formId || undefined,
                prefillData: showPrefill ? {
                    companyName: formData.prefillData.companyName.trim() || undefined,
                    industry: formData.prefillData.industry || undefined,
                    contactName: formData.prefillData.contactName.trim() || undefined,
                    phone: formData.prefillData.phone.trim() || undefined
                } : undefined
            };

            const response = await createOnboardingLink(request);
            
            if (response && response.url) {
                setResult(response);
                
                // Reset form
                setFormData({
                    clientEmail: '',
                    clientName: '',
                    formId: formData.formId, // Keep selected form
                    prefillData: {
                        companyName: '',
                        industry: '',
                        contactName: '',
                        phone: ''
                    }
                });
                setShowPrefill(false);
            } else if (response && response.error === 'duplicate_onboarding_request') {
                // Backend detected duplicate - show the backend's message
                setError(response.message || 'An active onboarding token already exists for this client.');
            } else {
                setError('Failed to create onboarding link');
            }
        } catch (err) {
            console.error('Create onboarding link error:', err);
            // Check if error response has a message from backend
            if (err.message && err.message.includes('duplicate_onboarding_request')) {
                setError('An active onboarding token already exists for this client. Please wait for it to expire or cancel it before sending a new request.');
            } else {
                setError(err.message || 'Failed to create onboarding link');
            }
        }
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        alert('Link copied to clipboard!');
    };

    const selectedForm = formData.formId 
        ? onboardingForms.find(f => f.formId === formData.formId)
        : null;

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <h2>Create Client Onboarding Link</h2>
                <p className={styles.subtitle}>
                    Generate a secure link to send to new clients for registration
                </p>
            </div>

            {(error || hookError) && (
                <div className={styles.errorAlert}>
                    <strong>Error:</strong> {error || hookError}
                </div>
            )}

            {result && (
                <div className={styles.successAlert}>
                    <h3>✓ Onboarding Link Created!</h3>
                    <p>An email has been sent to <strong>{result.recipientEmail}</strong></p>
                    
                    <div className={styles.resultDetails}>
                        <div className={styles.resultItem}>
                            <label>Link URL:</label>
                            <div className={styles.urlContainer}>
                                <code className={styles.url}>{result.url}</code>
                                <button 
                                    type="button"
                                    className={styles.copyButton}
                                    onClick={() => copyToClipboard(result.url)}
                                >
                                    Copy
                                </button>
                            </div>
                        </div>
                        <div className={styles.resultItem}>
                            <label>Expires:</label>
                            <span>{new Date(result.expiresAt).toLocaleString()}</span>
                        </div>
                        {result.formId && (
                            <div className={styles.resultItem}>
                                <label>Form:</label>
                                <span>{selectedForm?.formName || result.formId}</span>
                            </div>
                        )}
                    </div>

                    <button 
                        type="button"
                        className={styles.dismissButton}
                        onClick={() => setResult(null)}
                    >
                        Create Another
                    </button>
                </div>
            )}

            <form onSubmit={handleSubmit} className={styles.form}>
                <div className={styles.section}>
                    <h3>Client Information</h3>
                    
                    <div className={styles.formGroup}>
                        <label htmlFor="clientEmail">
                            Client Email <span className={styles.required}>*</span>
                        </label>
                        <input
                            type="email"
                            id="clientEmail"
                            value={formData.clientEmail}
                            onChange={(e) => setFormData({ ...formData, clientEmail: e.target.value })}
                            placeholder="client@example.com"
                            required
                        />
                        <span className={styles.helpText}>
                            The onboarding link will be sent to this email
                        </span>
                    </div>

                    <div className={styles.formGroup}>
                        <label htmlFor="clientName">Client Name</label>
                        <input
                            type="text"
                            id="clientName"
                            value={formData.clientName}
                            onChange={(e) => setFormData({ ...formData, clientName: e.target.value })}
                            placeholder="Acme Corp"
                        />
                        <span className={styles.helpText}>
                            Optional: Used in the email greeting
                        </span>
                    </div>
                </div>

                <div className={styles.section}>
                    <h3>Form Selection</h3>
                    
                    <div className={styles.formGroup}>
                        <label htmlFor="formId">Onboarding Form</label>
                        {formsLoading ? (
                            <p className={styles.loading}>Loading forms...</p>
                        ) : (
                            <>
                                <select
                                    id="formId"
                                    value={formData.formId}
                                    onChange={(e) => setFormData({ ...formData, formId: e.target.value })}
                                >
                                    <option value="">Use Default Form</option>
                                    {onboardingForms.map(form => (
                                        <option key={form.formId} value={form.formId}>
                                            {form.formName} {form.isDefault ? '(Default)' : ''} - v{form.version}
                                        </option>
                                    ))}
                                </select>
                                {selectedForm && (
                                    <span className={styles.helpText}>
                                        {selectedForm.description || 'Selected form'}
                                        {' • '}
                                        {selectedForm.formSchema?.schema?.fields?.length || 0} fields
                                        {selectedForm.expiryHours && ` • Token expires in ${selectedForm.expiryHours}h`}
                                    </span>
                                )}
                            </>
                        )}
                    </div>
                </div>

                <div className={styles.section}>
                    <div className={styles.sectionHeader}>
                        <h3>Prefill Data</h3>
                        <label className={styles.toggleLabel}>
                            <input
                                type="checkbox"
                                checked={showPrefill}
                                onChange={(e) => setShowPrefill(e.target.checked)}
                            />
                            Enable prefilling
                        </label>
                    </div>

                    {showPrefill && (
                        <div className={styles.prefillFields}>
                            <p className={styles.prefillHint}>
                                Optional: Prefill form fields to make onboarding easier for the client
                            </p>

                            <div className={styles.formRow}>
                                <div className={styles.formGroup}>
                                    <label htmlFor="companyName">Company Name</label>
                                    <input
                                        type="text"
                                        id="companyName"
                                        value={formData.prefillData.companyName}
                                        onChange={(e) => setFormData({
                                            ...formData,
                                            prefillData: { ...formData.prefillData, companyName: e.target.value }
                                        })}
                                        placeholder="Acme Corporation"
                                    />
                                </div>

                                <div className={styles.formGroup}>
                                    <label htmlFor="industry">Industry</label>
                                    <select
                                        id="industry"
                                        value={formData.prefillData.industry}
                                        onChange={(e) => setFormData({
                                            ...formData,
                                            prefillData: { ...formData.prefillData, industry: e.target.value }
                                        })}
                                    >
                                        <option value="">Select...</option>
                                        <option value="Pharmaceuticals">Pharmaceuticals</option>
                                        <option value="Biotechnology">Biotechnology</option>
                                        <option value="Academic Research">Academic Research</option>
                                        <option value="Healthcare">Healthcare</option>
                                        <option value="Other">Other</option>
                                    </select>
                                </div>
                            </div>

                            <div className={styles.formRow}>
                                <div className={styles.formGroup}>
                                    <label htmlFor="contactName">Contact Name</label>
                                    <input
                                        type="text"
                                        id="contactName"
                                        value={formData.prefillData.contactName}
                                        onChange={(e) => setFormData({
                                            ...formData,
                                            prefillData: { ...formData.prefillData, contactName: e.target.value }
                                        })}
                                        placeholder="John Doe"
                                    />
                                </div>

                                <div className={styles.formGroup}>
                                    <label htmlFor="phone">Phone</label>
                                    <input
                                        type="tel"
                                        id="phone"
                                        value={formData.prefillData.phone}
                                        onChange={(e) => setFormData({
                                            ...formData,
                                            prefillData: { ...formData.prefillData, phone: e.target.value }
                                        })}
                                        placeholder="+1-555-0123"
                                    />
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                <div className={styles.actions}>
                    <button type="submit" className={styles.submitButton} disabled={loading}>
                        {loading ? 'Creating...' : 'Create & Send Onboarding Link'}
                    </button>
                </div>
            </form>

            <div className={styles.infoPanel}>
                <h3>How it works</h3>
                <ol>
                    <li>Enter the client's email address and optional information</li>
                    <li>Select which onboarding form to use (or use the default)</li>
                    <li>Optionally prefill form data to save the client time</li>
                    <li>Click "Create & Send" - an email with the link is sent automatically</li>
                    <li>The client clicks the link and completes their registration</li>
                    <li>The link expires in 72 hours and can only be used once</li>
                </ol>
            </div>
        </div>
    );
};

export default OnboardingLinkCreator;
